/**
 * The DPS preview's ephemeral edit session (08-PRV §5.3). Binds the SHARED edit layer
 * ({@link useEditLayer}) to a form-scoped ephemeral atom, so drags / stacked picks /
 * bulk shifts / reverts behave exactly as in the eval dialog — but the commit writes
 * only to the in-memory session, never to any store or the save payload.
 *
 * Lifecycle:
 *  - Cleared on recompute: when a new preview payload lands (any definition change,
 *    incl. via the capture atom's poll), the adjustments referenced a schedule that no
 *    longer exists, so the session is dropped; if it carried adjustments, the discarded
 *    count is surfaced for the notice.
 *  - Cleared on leave: the session atom is reset when the preview unmounts (the form is
 *    left), so nothing survives navigation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useEditLayer,
  type BulkShiftResult,
  type MergedDate,
  type MilestoneKey,
  type RevertScope,
  type ScheduleOverrideEntry,
  type ScheduleRow,
  type SlotId,
} from 'pages/packages/show/eval3-new/schedule-dialog';
import type { SchedulePreviewPayload } from 'types/dps-capture';
import { ephemeralScheduleAtom } from './schedule-preview-atoms';

export interface EphemeralScheduleApi {
  /** The current ephemeral override session. */
  workingEntries: ScheduleOverrideEntry[];
  /** How many milestone dates the analyst has set ("N preview adjustments"). */
  adjustmentCount: number;
  /** Appends a single date override (drag / stacked pick). */
  applyDrag: (slotId: SlotId, field: MilestoneKey, isoDay: string) => void;
  /**
   * Multi-marker drag: commits the grabbed marker's date AND shifts every other selected
   * point by the same delta in one transform (each clamped to its own lifecycle bounds).
   */
  applyMultiDrag: (
    rows: ScheduleRow[],
    primary: { slotId: SlotId; field: MilestoneKey; iso: string },
    others: Array<{ slotId: SlotId; field: MilestoneKey }>,
    deltaMs: number
  ) => void;
  /** Relative-month bulk shift over selected points; returns the applied/skip summary. */
  bulkShift: (
    rows: ScheduleRow[],
    points: Array<{ slotId: SlotId; field: MilestoneKey }>,
    offsetMonths: number
  ) => BulkShiftResult;
  /** Reverts at milestone / slot / selection / whole-schedule granularity. */
  revertScope: (scope: RevertScope) => void;
  /** Clears every adjustment ("Reset all"). */
  resetAll: () => void;
  /** The row's effective dates (base overlaid with the session's "Set by you" edits). */
  effectiveDates: (row: ScheduleRow) => Record<MilestoneKey, MergedDate>;
  /** The count discarded by the most recent recompute, or null when there is no notice. */
  discardedCount: number | null;
  /** Dismisses the "N adjustments discarded" notice. */
  dismissDiscardedNotice: () => void;
}

/**
 * The ephemeral schedule-edit session for the DPS preview.
 *
 * @param {SchedulePreviewPayload | null} preview - The current capture preview payload
 *   (a new object identity means a recompute landed).
 * @return {EphemeralScheduleApi} The edit API + recompute-discard notice.
 */
export function useEphemeralSchedule(
  preview: SchedulePreviewPayload | null
): EphemeralScheduleApi {
  const layer = useEditLayer(ephemeralScheduleAtom);
  const [discardedCount, setDiscardedCount] = useState<number | null>(null);

  // The previous payload identity (recompute detector) and the live adjustment count,
  // read inside the recompute effect without adding them as deps.
  const prevPreviewRef = useRef<SchedulePreviewPayload | null>(null);
  const adjustmentCountRef = useRef(0);
  adjustmentCountRef.current = layer.editedDateCount;
  const clearSession = layer.setEntries;

  // Cleared on recompute: a new payload identity discards the session. A non-empty
  // session raises the discarded-count notice; an empty one clears any prior notice, so
  // the notice always reflects only the most recent recompute.
  useEffect(() => {
    const previous = prevPreviewRef.current;
    if (previous !== null && previous !== preview) {
      setDiscardedCount(
        adjustmentCountRef.current > 0 ? adjustmentCountRef.current : null
      );
      clearSession([]);
    }
    prevPreviewRef.current = preview;
  }, [preview, clearSession]);

  // Cleared on leave: disposing the preview resets the form-scoped session.
  useEffect(
    () => () => {
      clearSession([]);
    },
    [clearSession]
  );

  const resetAll = useCallback(() => {
    layer.revertScope({ kind: 'all' });
  }, [layer]);

  const dismissDiscardedNotice = useCallback(() => setDiscardedCount(null), []);

  return {
    workingEntries: layer.workingEntries,
    adjustmentCount: layer.editedDateCount,
    applyDrag: layer.applyDrag,
    applyMultiDrag: layer.applyMultiDrag,
    bulkShift: layer.bulkShift,
    revertScope: layer.revertScope,
    resetAll,
    effectiveDates: layer.effectiveDates,
    discardedCount,
    dismissDiscardedNotice,
  };
}
